import { useState, useEffect, useMemo, useRef } from "react";
import ConsoleLayout from "@/components/ConsoleLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { adminApi } from "@/lib/api";
import { adaptUsageRecord } from "@/lib/adapters";
import type { AdaptedUsageLog, AdminTeamSummary } from "@/lib/types";
import { UsageLogRow } from "@/components/UsageLogRow";
import { Download, ChevronLeft, ChevronRight, Check } from "lucide-react";
import { exportToCsv } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { DatePicker } from "@/components/ui/date-picker";

// Local calendar date (YYYY-MM-DD) for the default "today 00:00 → now" range.
function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Cross-team call-log page for system_admin. Backed by GET /api/v1/admin/usage
// which returns the full upstream view (Upstream/Provider/per-dim pricing all
// visible — no redaction). Records span every team; a Team column is rendered
// per row using the admin teams listing to resolve team_id → name.
export default function AdminUsage() {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [logs, setLogs] = useState<AdaptedUsageLog[]>([]);
  const [teams, setTeams] = useState<AdminTeamSummary[]>([]);
  const [loading, setLoading] = useState(true);

  // Draft inputs — edited freely, only committed on Query click
  const [model, setModel] = useState("");
  const [requestId, setRequestId] = useState("");
  const [status, setStatus] = useState("");
  const [teamFilter, setTeamFilter] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [startDate, setStartDate] = useState(todayStr());
  const [endDate, setEndDate] = useState(todayStr());

  // Applied filters — the ONLY values fetchLogs reads
  const [appliedModel, setAppliedModel] = useState("");
  const [appliedRequestId, setAppliedRequestId] = useState("");
  const [appliedStatus, setAppliedStatus] = useState("");
  const [appliedTeamFilter, setAppliedTeamFilter] = useState("");
  const [appliedUserFilter, setAppliedUserFilter] = useState("");
  const [appliedStartDate, setAppliedStartDate] = useState(todayStr());
  const [appliedEndDate, setAppliedEndDate] = useState(todayStr());

  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [justQueried, setJustQueried] = useState(false);
  const justQueriedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const PAGE_SIZE = 50;

  const buildParams = (limit: number, offset: number) => ({
    limit,
    offset,
    team_id: appliedTeamFilter || undefined,
    user_id: appliedUserFilter || undefined,
    model: appliedModel || undefined,
    request_id: appliedRequestId || undefined,
    status: appliedStatus || undefined,
    after: appliedStartDate ? new Date(appliedStartDate + "T00:00:00").toISOString() : undefined,
    before: appliedEndDate ? new Date(appliedEndDate + "T23:59:59").toISOString() : undefined,
  });

  const runQuery = () => {
    if (justQueried) return; // 对号显示期间忽略重复点击/回车
    setAppliedModel(model);
    setAppliedRequestId(requestId);
    setAppliedStatus(status);
    setAppliedTeamFilter(teamFilter);
    setAppliedUserFilter(userFilter);
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setPage(0);
    setReloadNonce((n) => n + 1);
    setJustQueried(true);
    if (justQueriedTimer.current) clearTimeout(justQueriedTimer.current);
    justQueriedTimer.current = setTimeout(() => setJustQueried(false), 1200);
  };

  // Clear the pending 对号 timer on unmount to avoid setState-after-unmount.
  useEffect(() => () => {
    if (justQueriedTimer.current) clearTimeout(justQueriedTimer.current);
  }, []);

  const teamMap = useMemo(() => {
    const m: Record<string, string> = {};
    teams.forEach((ts) => { m[ts.team.id] = ts.team.name; });
    return m;
  }, [teams]);

  // Load every team once for the dropdown + name lookup. page_size 200 covers
  // the realistic ceiling; admin can always filter by team_id text if larger.
  useEffect(() => {
    if (!user?.is_system_admin) return;
    adminApi.listAllTeams({ page: 1, page_size: 200 })
      .then((resp) => setTeams(resp.teams))
      .catch(() => { /* ignore */ });
  }, [user?.is_system_admin]);

  const fetchLogs = async () => {
    if (!user?.is_system_admin) return;
    setLoading(true);
    try {
      const usage = await adminApi.listUsage(buildParams(PAGE_SIZE, page * PAGE_SIZE));
      setLogs(usage.records.map((r) => adaptUsageRecord(r, usage.api_key_names || {}, language)));
      setTotal(usage.total);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.is_system_admin, page, reloadNonce]);

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const [exporting, setExporting] = useState(false);

  const renderTeamCell = (log: AdaptedUsageLog) => {
    const name = log.teamId ? teamMap[log.teamId] : undefined;
    return (
      <div className="min-w-0">
        <div className="text-xs text-slate-300 truncate">{name || "—"}</div>
        <div className="text-[10px] text-slate-600 font-mono truncate">{log.userId || "—"}</div>
      </div>
    );
  };

  const handleExportCsv = async () => {
    if (total === 0) return;
    setExporting(true);
    try {
      const allUsage = await adminApi.listUsage(buildParams(total, 0));
      const allLogs = allUsage.records.map((r) => adaptUsageRecord(r, allUsage.api_key_names || {}, language));
      exportToCsv(
        `admin-call-logs-${new Date().toISOString().slice(0, 10)}.csv`,
        ["Request ID", "Model", "Upstream Model", "Provider", "Type", "Status", "Timestamp", "Input Tokens", "Output Tokens", "Cache Read Tokens", "Cache Write Tokens", "Total Tokens", "Latency (ms)", "Cost (USD)", "API Key", "Team", "User ID", "Error", "Error Detail"],
        allLogs.map((l) => [l.requestId, l.model, l.upstreamModel || "", l.provider || "", l.type, l.status, l.timestamp, String(l.inputTokens || 0), String(l.outputTokens || 0), String(l.cacheReadTokens || 0), String(l.cacheWriteTokens || 0), String(l.tokens), String(l.latency), l.cost, l.keyName, l.teamId ? (teamMap[l.teamId] || l.teamId) : "", l.userId || "", l.errorMessage || "", l.errorDetail || ""])
      );
    } catch {
      // ignore
    } finally {
      setExporting(false);
    }
  };

  return (
    <ConsoleLayout
      title={t("logs.admin.title")}
      subtitle={t("logs.admin.subtitle")}
      actions={
        <Button size="sm" variant="ghost" className="gap-1.5 text-xs h-8 text-slate-400 border border-white/10" onClick={handleExportCsv} disabled={total === 0 || exporting}>
          <Download size={12} />
          {exporting ? "..." : t("logs.exportCsv")}
        </Button>
      }
    >
      <div className="p-6 space-y-5">
        {/* Filters: Time — Model — RequestId — Status — Team — User — Query */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <DatePicker value={startDate} onChange={setStartDate} />
            <span className="text-xs text-slate-600">—</span>
            <DatePicker value={endDate} onChange={setEndDate} />
          </div>
          <Input
            placeholder={t("logs.searchModelPlaceholder")}
            value={model}
            onChange={(e) => setModel(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") runQuery(); }}
            className="flex-1 min-w-[160px] max-w-[220px] bg-white/5 border-white/10 text-sm h-9 text-white placeholder:text-slate-600"
          />
          <Input
            placeholder={t("logs.searchRequestIdPlaceholder")}
            value={requestId}
            onChange={(e) => setRequestId(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") runQuery(); }}
            maxLength={26}
            className="flex-1 min-w-[160px] max-w-[240px] bg-white/5 border-white/10 text-sm h-9 text-white placeholder:text-slate-600 font-mono"
          />
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white h-9 focus:outline-none focus:border-blue-500/50"
          >
            <option value="">{t("logs.statusAll")}</option>
            <option value="success">{t("logs.statusSuccess")}</option>
            <option value="failed">{t("logs.statusFailed")}</option>
            <option value="running">{t("logs.statusRunning")}</option>
          </select>
          <select
            value={teamFilter}
            onChange={(e) => setTeamFilter(e.target.value)}
            className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white h-9 focus:outline-none focus:border-blue-500/50 max-w-[200px]"
          >
            <option value="">{t("logs.admin.allTeams")}</option>
            {teams.map((ts) => (
              <option key={ts.team.id} value={ts.team.id}>
                {ts.team.name}
              </option>
            ))}
          </select>
          <Input
            placeholder={t("logs.admin.filterByUser")}
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") runQuery(); }}
            maxLength={26}
            className="bg-white/5 border-white/10 text-xs h-9 text-white placeholder:text-slate-600 max-w-[200px] font-mono"
          />
          <Button size="sm" variant="ghost" onClick={runQuery} className="relative h-9 px-4 text-xs text-slate-300 border border-white/10 active:bg-white/10">
            <span className={justQueried ? "invisible" : undefined}>{t("logs.applyFilters")}</span>
            {justQueried && (
              <span className="absolute inset-0 flex items-center justify-center text-emerald-400">
                <Check size={14} />
              </span>
            )}
          </Button>
          <div className="ml-auto flex items-center gap-3">
            <span className="text-xs text-slate-500">{total} {t("logs.records")}</span>
            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                  className="p-1 rounded text-slate-500 hover:text-white disabled:opacity-30"
                >
                  <ChevronLeft size={14} />
                </button>
                <span className="text-xs text-slate-400">{page + 1} / {totalPages}</span>
                <button
                  onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                  disabled={page >= totalPages - 1}
                  className="p-1 rounded text-slate-500 hover:text-white disabled:opacity-30"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Logs Table */}
        <div className="sci-card overflow-hidden">
          <div className="px-5 py-3 border-b border-white/8 grid grid-cols-[24px_1fr_160px_120px_80px_20px] gap-4 text-[10px] text-slate-600 uppercase tracking-wider items-center">
            <div />
            <div>{t("logs.modelRequest")}</div>
            <div className="hidden lg:block">{t("logs.admin.team")} / {t("logs.admin.user")}</div>
            <div className="hidden sm:block text-right">{t("logs.usage")}</div>
            <div className="text-right">{t("logs.cost")}</div>
            <div />
          </div>
          {logs.length > 0 ? (
            logs.map((log) => (
              <UsageLogRow
                key={log.id}
                log={log}
                extraColumn={renderTeamCell(log)}
                extraDetailFields={
                  [
                    log.teamId && ["Team", `${teamMap[log.teamId] || "—"} (${log.teamId})`],
                    log.userId && ["User ID", log.userId],
                  ].filter(Boolean) as Array<[string, string]>
                }
              />
            ))
          ) : (
            <div className="py-16 text-center text-slate-500 text-sm">
              {loading ? "Loading..." : t("logs.noLogs")}
            </div>
          )}
        </div>
      </div>
    </ConsoleLayout>
  );
}
